import Input from "../../controls/Input";
import TextArea from "../../controls/TextArea";
import SelectMenu from "../../controls/SelectMenu";
import { useForm } from "../useForm";
import { useEffect, useState } from "react";
import { formatDateWithTime, convertToDefEventPara } from "../../../shared/utility";
import ButtonSpinner from "../../controls/ButtonSpinner";
import MediaUi from "../../importMedia/MediaUi";
import CameraCaptureModal from "../../camera/CameraCaptureModal";
import EmptyModal from "../../modals/EmptyModal";

const initialFValues = {
  name: "",
  email: "",
  company: "",
  phone: "",
  contact: "",
  reason: "",
  checked_in_date: "",
  checked_out_date: "",
  picture: null,
  visitor_type: { value: -1, label: "Select type" },
}

export default function VisitorLogForm(props) {
  const {
    inProgress,
    visitor,
    updateVisitor,
    checkoutVisitor,
    onCreateVisitor,
    onCancel,
    visitorTypeOptions,
  } = props;

  const [uploadedVisitorImage, setUploadedVisitorImage] = useState(null);
  const [cameraCapturedFile, setCameraCapturedFile] = useState(null);
  const [cameraModalOpen, setCameraModalOpen] = useState({
    open: false,
    childComponent: null,
  });

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false,
    null
  );

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

      temp.name = null;
      if (!values.name || values.name.trim() === "") {
        temp.name = "This field is required.";
      }

      temp.company = null;
      if (!values.company || values.company.trim() === "") {
        temp.company = "This field is required.";
      }

      temp.reason = null;
      if (!values.reason || values.reason.trim() === "") {
        temp.reason = "This field is required.";
      }

      temp.visitor_type = null;
      if (!values.visitor_type || values.visitor_type.value === -1) {
        temp.visitor_type = "This field is required.";
      }

      setErrors({
        ...temp,
      });
  
      if (fieldValues === values) {
        return Object.values(temp).every((x) => x === null);
      }
    };

  useEffect(() => {
    if (visitor) {
      setValues({
        ...values,
        ...visitor,
      });
      // Reset camera captured file when loading existing visitor
      setCameraCapturedFile(null);
    }
  }, [visitor]);

  const onCheckoutVisitor = async () => {
    const res = await checkoutVisitor();
    if (res.success) {
      setValues({
        ...values,
        checked_out_date: res.data.checked_out_date,
      });
    }
  };

  const onSave = async (payload) => {
    if (validate()) {
      // Get file from camera capture or from payload.picture array
      const pictureFile = cameraCapturedFile || (Array.isArray(payload.picture) && payload.picture.length > 0 ? payload.picture[0] : null);
      
      if (pictureFile && pictureFile instanceof File) {
        delete payload.picture; // Remove picture field (backend expects string or not sent)
        payload.uploaded_picture = [pictureFile]; // Backend expects uploaded_picture[0]
      } else {
        // No file - remove picture if it's an array, null, empty string, or a string URL
        // (existing picture URLs should not be sent when no new file is uploaded)
        if (Array.isArray(payload.picture) || payload.picture === null || payload.picture === "" || typeof payload.picture === "string") {
          delete payload.picture;
        }
      }
      
      if (visitor) {
        updateVisitor(payload);
      } else {
        onCreateVisitor(payload);
      }
    }
  };

  const handleCameraCapture = (file) => {
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setUploadedVisitorImage(previewUrl);

    // Store the file for later use in onSave
    setCameraCapturedFile(file);

    // Pass to form handler (same format as ImportMediaDropzone)
    handleInputChange(convertToDefEventPara("picture", [file]));

    // Close modal
    setCameraModalOpen({
      open: false,
      childComponent: null,
    });
  };

  const openCameraModal = () => {
    setCameraModalOpen({
      open: true,
      childComponent: (
        <CameraCaptureModal
          open={true}
          setOpen={setCameraModalOpen}
          onCapture={handleCameraCapture}
        />
      ),
    });
  };

  return (
    <div className="justify-between">
        <h1 className="text-2xl font-semibold leading-6 text-gray-900 mb-4">
          {visitor ? `Edit Visitor: ${visitor.name}` : "Create Visitor"}
        </h1>

      <div className="pt-6 pb-5 grid grid-cols-1 gap-2">
        <div className="p-4 card-custom space-y-5">
          <div className="grid sm:grid-cols-4 gap-4">
            <Input
              label="Visitor Name"
              name="name"
              value={values.name }
              labelOn={true}
              type="text"
              className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm ${
                errors.name
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300"
              }`}
              onChange={handleInputChange}
              error={errors.name}
            />

            <Input
              label="Email"
              name="email"
              value={values.email }
              labelOn={true}
              type="email"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              onChange={handleInputChange}
            />

            <Input
              label="Company"
              name="company"
              value={values.company }
              labelOn={true}
              type="text"
              className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm ${
                errors.company
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300"
              }`}
              onChange={handleInputChange}
              error={errors.company}
            />

            <Input
              label="Phone"
              name="phone"
              value={values.phone }
              labelOn={true}
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              onChange={handleInputChange}
            />
            
            <SelectMenu
                handleInputChange={handleInputChange}
                name="visitor_type"
                options={visitorTypeOptions}
                value={values.visitor_type}
                title="Visitor Type"
                error={errors.visitor_type}
                isClearable={true}
                placeholder="Select visitor type"
              />

              <Input
                label="Contact Name"
                name="contact"
                value={values.contact}
                labelOn={true}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                onChange={handleInputChange}
              />
          </div>

          {visitor && (
            <div className="grid sm:grid-cols-4 gap-4">
              <Input
                label="Checked In"
                name="checked_in_date"
                value={values.checked_in_date ? formatDateWithTime(values.checked_in_date) : ""}
                labelOn={true}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                disabled={true}
              />

              <Input
                label="Checked Out"
                name="checked_out_date"
                value={
                  values.checked_out_date
                    ? formatDateWithTime(values.checked_out_date)
                    : "Not checked out"
                }
                labelOn={true}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                disabled={true}
              />
            </div>
          )}

        <div>
          <MediaUi
            title="Visitor Image"
            uploadedImage={uploadedVisitorImage}
            setUploadedImage={(imageArray) => {
              setUploadedVisitorImage(imageArray && imageArray.length > 0 ? imageArray[0] : null);
            }}
            isEdit={visitor ? true : false}
            imageUrl={values.picture_url}
            imageName="picture"
            handleInputChange={handleInputChange}
            onDelete={() => {
              setValues({
                ...values,
                picture: null,
                picture_url: null,
              });
              setUploadedVisitorImage(null);
              setCameraCapturedFile(null); // Clear camera captured file when deleting
            }}
          />
          <div className="flex mt-2 gap-2">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md bg-gray-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
              onClick={openCameraModal}
            >
              Take picture from camera
            </button>
          </div>
        </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <TextArea
              label="Reason"
              name="reason"
              value={values.reason }
              labelOn={true}
              rows={4}
              className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm ${
                errors.reason
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300"
              }`}
              onChange={handleInputChange}
              error={errors.reason}
            />
              
          </div>
        </div>

        <div className="flex gap-3">
          {visitor ? (
            <>
              <button
                type="button"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center mt-3"
                onClick={()=>onSave(values)}
                disabled={inProgress}
              >
                <ButtonSpinner spin={inProgress} text="Save" />
              </button>

              {!values.checked_out_date && (
                <button
                  type="button"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 items-center mt-3"
                  onClick={onCheckoutVisitor}
                  disabled={inProgress}
                >
                  <ButtonSpinner spin={inProgress} text="Mark as Checked Out" />
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center"
                onClick={()=>onSave(values)}
                disabled={inProgress}
              >
                <ButtonSpinner spin={inProgress} text="Create" />
              </button>
              <button
                type="button"
                className="ml-3 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md items-center"
                onClick={onCancel}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <EmptyModal
        childComponent={cameraModalOpen.childComponent}
        open={cameraModalOpen.open}
        setOpen={setCameraModalOpen}
      />
    </div>
  );
}
